import { useCallback, useContext, useEffect, useMemo } from 'react';
import type { ChatSocketEvent, SocketNamespace } from '@trinity/chat';
import { LifeCycleSocketEvent } from '@trinity/chat';
import type { Socket } from 'socket.io-client';
import { IsDemoEnvContext } from '@trinity/chat-components/containers/Chat/contexts.js';
import { createLogger } from '../utils';

export const useSetupDefaultSocketListeners = (socket: Socket, namespace: SocketNamespace) => {

  const isDevEnv = useContext(IsDemoEnvContext);

  const logger = useMemo(() => createLogger({
    isDevEnv,
    namespace,
  }), [isDevEnv, namespace]);

  const logEvents = useCallback((event: ChatSocketEvent, payload: unknown) => logger.event(event, payload), [logger]);
  const handleConnect = useCallback(() => logger.event(LifeCycleSocketEvent.Connect), [logger]);
  const handleConnectError = useCallback((err: Error) => logger.event(LifeCycleSocketEvent.ConnectError, err), [logger]);
  const handleDisconnect = useCallback(() => logger.event(LifeCycleSocketEvent.Disconnect), [logger]);

  useEffect(() => {
    if (!socket) return;

    socket.on(LifeCycleSocketEvent.Connect, handleConnect);
    socket.on(LifeCycleSocketEvent.ConnectError, handleConnectError);
    socket.on(LifeCycleSocketEvent.Disconnect, handleDisconnect);

    socket.prependAny(logEvents);

    return () => {
      socket.off(LifeCycleSocketEvent.Connect, handleConnect);
      socket.off(LifeCycleSocketEvent.ConnectError, handleConnectError);
      socket.off(LifeCycleSocketEvent.Disconnect, handleDisconnect);

      socket.offAny(logEvents);
    };
  }, [
    handleConnect,
    handleConnectError,
    handleDisconnect,
    logEvents,
    socket,
  ]);
};